import { useState, useEffect, useCallback } from 'react'
import { supabaseClient } from '../config/supabase'

// ── Types ─────────────────────────────────────────────────────
export interface PeoAlignmentRow {
  program_code: string
  program_name: string
  department_name: string
  outcome_id: string
  outcome_type: 'PEO' | 'PO'
  peo_number: number
  title: string
  avg_score: number
  respondent_count: number
  attainment_rate: number
}

export interface TracerStudyStats {
  total_alumni: number
  total_employed: number
  employment_rate: number
  employment_by_status: Record<string, number>
  salary_distribution: Record<string, number>
  job_relevance_distribution: Record<string, number>
  employer_type_distribution: Record<string, number>
  employment_length_distribution: Record<string, number>
  top_fields_of_work: { field: string; count: number }[]
  top_skills: { skill: string; count: number }[]
  unemployment_reasons: { reason: string; count: number }[]
}

export interface TracerTrendRow {
  year_graduated: number
  total_alumni: number
  employed_count: number
  employment_rate: number
  avg_peo_score: number | null
}

export interface DepartmentComparisonRow {
  department_name: string
  total_alumni: number
  employed_count: number
  employment_rate: number
  avg_peo_score: number | null
  job_relevance_rate: number | null
  top_program: string | null
}

export interface ProgramComparisonRow {
  program_code: string
  program_name: string
  department_name: string
  total_alumni: number
  employed_count: number
  employment_rate: number
  avg_peo_score: number | null
  avg_po_score: number | null
  job_relevance_rate: number | null
  salary_above_40k_rate: number | null
}

interface HookResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => void
}

// ── Hook factory ──────────────────────────────────────────────
function useRpc<T>(
  rpcName: string,
  params: Record<string, any>,
  deps: any[]
): HookResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: result, error: rpcError } = await supabaseClient.rpc(rpcName, params)
      if (rpcError) throw rpcError
      setData(result as T)
    } catch (err: any) {
      console.error(`RPC ${rpcName} error:`, err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => { fetch() }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// ── Exported hooks ────────────────────────────────────────────
export function usePeoAlignmentStats(
  department: string | null,
  program: string | null,
  batchYear: number | null
): HookResult<PeoAlignmentRow[]> {
  return useRpc<PeoAlignmentRow[]>(
    'get_peo_alignment_stats',
    {
      p_department: department || null,
      p_program: program || null,
      p_batch_year: batchYear || null,
    },
    [department, program, batchYear]
  )
}

export function useTracerStudyStats(
  department: string | null,
  program: string | null,
  batchYear: number | null
): HookResult<TracerStudyStats> {
  return useRpc<TracerStudyStats>(
    'get_tracer_study_stats',
    {
      p_department: department || null,
      p_program: program || null,
      p_batch_year: batchYear || null,
    },
    [department, program, batchYear]
  )
}

export function useTracerTrends(
  department: string | null
): HookResult<TracerTrendRow[]> {
  return useRpc<TracerTrendRow[]>(
    'get_tracer_study_trends',
    { p_department: department || null },
    [department]
  )
}

export function useDepartmentComparison(): HookResult<DepartmentComparisonRow[]> {
  return useRpc<DepartmentComparisonRow[]>(
    'get_department_comparison_stats',
    {},
    []
  )
}

export function useProgramComparison(
  department: string | null,
  programCodes: string[] | null
): HookResult<ProgramComparisonRow[]> {
  return useRpc<ProgramComparisonRow[]>(
    'get_program_comparison_stats',
    {
      p_department: department || null,
      p_program_codes: programCodes && programCodes.length > 0 ? programCodes : null,
    },
    [department, programCodes?.join(',')]
  )
}
